import React, { useState } from "react";
import { TextInput, StyleSheet, View, Pressable } from "react-native";
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, MagnifyingGlassIcon, InfoIcon } from "@phosphor-icons/react";
import CustomText from "../CustomText/CustomText";

function CustomTextInput({
  inputType = "text",
  value,
  onChangeText,
  placeholder,
  style = {},
  secureTextEntry = false,
  onToggleSecureEntry = undefined,
  error = undefined,
  helperText = undefined,
  useTranslationText = false,
  ...props
}) {
  const [isActive, setIsActive] = useState(false);
  const isEmail = inputType === "email";
  const isSearch = inputType === "search";
  const hasSecureToggle = typeof onToggleSecureEntry === "function";
  const hasError = error != null && error !== "";
  const combinedStyles = [
    styles.input,
    style,
    (isEmail || isSearch) && styles.hasPrefixIcon,
    hasSecureToggle && styles.hasSuffixIcon,
    hasError && styles.errorInput, // Keep single error style
    hasSecureToggle && hasError && styles.hasDoubleIcon, // Add double padding if both
    isActive && styles.activeInput,
    hasError && isActive && styles.errorInputActive,
  ];

  const handleToggleSecureEntry = () => {
    if (hasSecureToggle) {
      onToggleSecureEntry();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.inputWrapper}>
        {isEmail && (
          <View style={styles.prefixIcon}>
            <EnvelopeIcon size={20} />
          </View>
        )}
        {isSearch && (
          <View style={styles.prefixIcon}>
            <MagnifyingGlassIcon size={20} />
          </View>
        )}
        <TextInput
          style={combinedStyles}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor="#A1A1A1"
          secureTextEntry={secureTextEntry}
          onFocus={() => {
            setIsActive(true);
          }}
          onBlur={() => {
            setIsActive(false);
          }}
          {...props}
        />
        {hasSecureToggle && (
          <Pressable
            onPress={handleToggleSecureEntry}
            style={styles.icon}
            accessibilityRole="button"
            accessibilityLabel={secureTextEntry ? "Show password" : "Hide password"}>
            {secureTextEntry ? <EyeIcon size={20} color="#6B7280" /> : <EyeSlashIcon size={20} color="#6B7280" />}
          </Pressable>
        )}
        {hasError && (
          <View style={[styles.icon, hasSecureToggle && styles.iconWithToggle]} aria-hidden="true">
            <InfoIcon size={16} color="#dc2626" weight="fill" />
          </View>
        )}
      </View>
      {helperText != null && helperText !== "" && (
        <CustomText style={styles.helperText} text={helperText} useTranslationText={useTranslationText} />
      )}
      {hasError && <CustomText style={styles.errorText} text={error} useTranslationText={useTranslationText} />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    color: "#1F2937",
    fontSize: 14,
    borderRadius: 8,
    width: "100%",
    padding: 10,
    outlineWidth: 2,
    outlineOffset: 2,
  },
  activeInput: {
    outlineColor: "#6788fe",
  },
  errorInputActive: {
    outlineColor: "#fee2e2",
    borderColor: "#ef4444",
  },
  errorInput: {
    borderColor: "#fca5a5",
  },
  inputWrapper: {
    position: "relative",
    flex: 1,
  },
  icon: {
    position: "absolute",
    right: 10,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  iconWithToggle: {
    right: 40,
  },
  hasPrefixIcon: {
    paddingLeft: 35,
  },
  hasSuffixIcon: {
    paddingRight: 35,
  },
  hasDoubleIcon: {
    paddingRight: 70,
  },
  prefixIcon: {
    position: "absolute",
    left: 10,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 12,
  },
  helperText: {
    fontSize: 12,
    color: "#667085",
  },
});

export default CustomTextInput;
